import { Interpretation, ClusterProposal } from './types'

// Pure client-side charity scoring (also re-computed / verified on server for persisted digs).
// The score rewards the difficult work of psychological distance + refusing to let clustering
// collapse ambiguity. It is *not* a measure of how "good" the climate choices in the shards were.

export interface CharityBreakdown {
  score: number
  distancePractice: number
  nuanceWork: number
  boundaryWork: number
  clusterCourage: number
  reflectionDepth: number
  notes: string[]
}

export function computeInterpretiveCharityScore(
  interps: Interpretation[],
  cluster?: ClusterProposal | null,
  manualEdits = 0,
  skips = 0
): CharityBreakdown {
  if (interps.length === 0) {
    return { score: 0, distancePractice: 0, nuanceWork: 0, boundaryWork: 0, clusterCourage: 0, reflectionDepth: 0, notes: ['No interpretations logged yet.'] }
  }

  let distancePractice = 0
  let nuanceWork = 0
  let reflectionDepth = 0

  const notes: string[] = []

  for (const i of interps) {
    // Distance practice: reward movement away from 5 (neutral) and spread across dimensions
    const vec = i.distances
    const spread = Object.values(vec).reduce((a, b) => a + Math.abs(b - 5), 0) / 4
    distancePractice += spread * 1.8

    // Nuance tags (each distinct tag is evidence of differentiation)
    nuanceWork += Math.min(i.nuanceTags.length, 5) * 2.4

    // Gloss length + time spent as reflection depth proxies (not raw length worship)
    const glossLen = i.gloss.trim().length
    reflectionDepth += Math.min(glossLen / 18, 7)
    reflectionDepth += Math.min(i.timeSpentMs / 14000, 5) // ~14s per meaningful minute of thought

    if (i.counterGloss && i.counterGloss.trim().length > 12) {
      nuanceWork += 1.8 // surfacing the harsh read then (presumably) choosing not to live there
    }
  }

  // Boundary and cluster courage — this is where "semantic clustering without erasing nuance" is scored
  const boundaryMarks = cluster?.boundaryCandidates?.length || 0
  const clusterEdits = manualEdits

  const boundaryWork = boundaryMarks * 4.2 + (clusterEdits > 0 ? clusterEdits * 5.5 : 0)

  // Courage penalty for blindly accepting a proposal with no edits when >4 interps
  let clusterCourage = 0
  if (interps.length >= 4) {
    if (clusterEdits === 0 && boundaryMarks < 2) {
      clusterCourage = -6
      notes.push('Low courage: accepted clustering proposal with almost no boundary work.')
    } else {
      clusterCourage = Math.min(clusterEdits * 3.5 + boundaryMarks * 1.8, 22)
    }
  }

  // Skip behaviour: thoughtful skips (we don't know intent, so small positive for low skips, small penalty for high)
  const skipAdj = skips > 3 ? -3 : skips === 0 ? 2 : 0

  const raw =
    38 +
    Math.min(distancePractice, 28) +
    Math.min(nuanceWork, 26) +
    Math.min(boundaryWork, 19) +
    clusterCourage +
    Math.min(reflectionDepth, 18) +
    skipAdj

  const score = Math.max(11, Math.min(96, Math.round(raw)))

  if (distancePractice > 18) notes.push('Strong movement across psychological distances.')
  if (nuanceWork > 14) notes.push('High differentiation via nuance tags and counter-reads.')
  if (boundaryWork > 11) notes.push('Significant manual defence of ambiguity and boundary cases.')
  if (clusterCourage > 8) notes.push('Refused to let the embedding proposal collapse the picture.')

  return {
    score,
    distancePractice: Math.round(Math.min(distancePractice, 28)),
    nuanceWork: Math.round(Math.min(nuanceWork, 26)),
    boundaryWork: Math.round(Math.min(boundaryWork, 19)),
    clusterCourage: Math.round(clusterCourage),
    reflectionDepth: Math.round(Math.min(reflectionDepth, 18)),
    notes: notes.length ? notes : ['Score will rise with distance shifts, specific glosses, and boundary marking.'],
  }
}

// Simple helper for a "ritual poverty signature" of a dig — higher = more evidence of thin or conflicted rituals
export function computeRitualPovertySig(interps: Interpretation[], totalShardsInTrench: number): number {
  if (interps.length === 0) return 42
  const coverage = interps.length / Math.max(totalShardsInTrench, 1)
  const avgNuance = interps.reduce((s, i) => s + i.nuanceTags.length, 0) / interps.length
  const avgTime = interps.reduce((s, i) => s + i.timeSpentMs, 0) / interps.length / 1000
  // low coverage + low nuance + low time = higher ritual poverty signal (the point is to notice it)
  const sig = Math.round(68 - (coverage * 18) - (avgNuance * 2.8) - Math.min(avgTime / 6, 11))
  return Math.max(19, Math.min(81, sig))
}
